import React, { useCallback, useMemo, useState } from 'react';
import {
  calendarGridDates,
  dayCoversSpan,
  formatCalendarMonthTitle,
  formatWeekRangeTitle,
  isSameLocalDay,
  isSpanEnd,
  isSpanStart,
  startOfLocalWeekSunday,
  weekDatesFromSundayStart,
} from '../utils/taskCalendarSpan';
import './ProjectCalendarTab.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type CalendarViewMode = 'month' | 'week';

export interface CalendarAppointment {
  /** Unique key for the event. */
  id: string;
  /** ISO datetime the appointment starts. */
  date: string;
  /**
   * ISO datetime the appointment ends. When set and on a later day than `date`,
   * the appointment is drawn on every day it covers instead of only its first.
   */
  endDate?: string | null;
  /** Short label shown on the calendar pill. */
  title: string;
  /** Accent color (hex/hsl) for the pill's left border. */
  color?: string;
  /** Extra tooltip detail. */
  subtitle?: string;
  onClick?: () => void;
}

export interface AppointmentCalendarLegendItem {
  label: string;
  color: string;
}

interface AppointmentCalendarProps {
  events: CalendarAppointment[];
  legend?: AppointmentCalendarLegendItem[];
  hint?: string;
}

function eventTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Midnight of the local calendar day an instant falls on.
 *
 * Note this deliberately does not reuse `startOfLocalDayFromTaskField`, which
 * reads the leading YYYY-MM-DD of the string: for a TIMESTAMPTZ instant that is
 * the UTC date, which puts a late-evening appointment on the following day.
 */
function localDayStart(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ events, legend, hint }) => {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [viewAnchor, setViewAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const goToMonthView = useCallback(() => {
    setViewMode('month');
    setViewAnchor((a) => new Date(a.getFullYear(), a.getMonth(), 1));
  }, []);

  const goToWeekView = useCallback(() => {
    setViewMode('week');
    setViewAnchor((a) => startOfLocalWeekSunday(a));
  }, []);

  const year = viewAnchor.getFullYear();
  const month0 = viewAnchor.getMonth();

  const cells = useMemo(() => {
    if (viewMode === 'week') return weekDatesFromSundayStart(viewAnchor);
    return calendarGridDates(year, month0);
  }, [viewMode, viewAnchor, year, month0]);

  const toolbarTitle =
    viewMode === 'week' ? formatWeekRangeTitle(viewAnchor) : formatCalendarMonthTitle(year, month0);

  const maxPerCell = viewMode === 'week' ? 16 : 4;

  /**
   * Each event paired with the inclusive range of calendar days it covers, in
   * start order. A single-day appointment has start === end.
   */
  const eventSpans = useMemo(() => {
    const out: { ev: CalendarAppointment; when: Date; span: { start: Date; end: Date } }[] = [];
    for (const ev of events) {
      const when = new Date(ev.date);
      if (Number.isNaN(when.getTime())) continue;
      const start = localDayStart(ev.date);
      if (!start) continue;
      const end = localDayStart(ev.endDate) ?? start;
      out.push({ ev, when, span: end.getTime() < start.getTime() ? { start: end, end: start } : { start, end } });
    }
    return out.sort((a, b) => a.when.getTime() - b.when.getTime());
  }, [events]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="project-cal-tab">
      <div className="project-cal-tab-toolbar">
        <button
          type="button"
          className="project-cal-tab-nav-btn"
          onClick={() =>
            setViewAnchor((prev) =>
              viewMode === 'month'
                ? new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                : (() => {
                    const d = new Date(prev);
                    d.setDate(d.getDate() - 7);
                    return d;
                  })()
            )
          }
          aria-label={viewMode === 'month' ? 'Previous month' : 'Previous week'}
        >
          ‹
        </button>
        <h2 className="project-cal-tab-toolbar-title">{toolbarTitle}</h2>
        <button
          type="button"
          className="project-cal-tab-nav-btn"
          onClick={() =>
            setViewAnchor((prev) =>
              viewMode === 'month'
                ? new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                : (() => {
                    const d = new Date(prev);
                    d.setDate(d.getDate() + 7);
                    return d;
                  })()
            )
          }
          aria-label={viewMode === 'month' ? 'Next month' : 'Next week'}
        >
          ›
        </button>
        <div className="project-cal-tab-view-toggle" role="group" aria-label="Calendar view">
          <button
            type="button"
            className={
              viewMode === 'month'
                ? 'project-cal-tab-view-toggle-btn project-cal-tab-view-toggle-btn--active'
                : 'project-cal-tab-view-toggle-btn'
            }
            onClick={goToMonthView}
            aria-pressed={viewMode === 'month'}
          >
            Month
          </button>
          <button
            type="button"
            className={
              viewMode === 'week'
                ? 'project-cal-tab-view-toggle-btn project-cal-tab-view-toggle-btn--active'
                : 'project-cal-tab-view-toggle-btn'
            }
            onClick={goToWeekView}
            aria-pressed={viewMode === 'week'}
          >
            Week
          </button>
        </div>
        <button
          type="button"
          className="project-cal-tab-today-btn"
          onClick={() => {
            const n = new Date();
            if (viewMode === 'month') {
              setViewAnchor(new Date(n.getFullYear(), n.getMonth(), 1));
            } else {
              setViewAnchor(startOfLocalWeekSunday(n));
            }
          }}
        >
          {viewMode === 'month' ? 'This month' : 'This week'}
        </button>
      </div>

      {legend && legend.length > 0 ? (
        <div className="appt-cal-legend" role="list">
          {legend.map((item) => (
            <span key={item.label} className="appt-cal-legend-item" role="listitem">
              <span className="appt-cal-legend-swatch" style={{ backgroundColor: item.color }} aria-hidden="true" />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className={viewMode === 'week' ? 'project-cal-expanded project-cal-expanded--week' : 'project-cal-expanded'}>
        {hint ? <p className="project-cal-expanded-hint">{hint}</p> : null}
        <div className="project-cal-expanded-weekdays">
          {WEEKDAYS.map((w) => (
            <div key={w} className="project-cal-expanded-weekday">
              {w}
            </div>
          ))}
        </div>
        <div className="project-cal-expanded-cells">
          {cells.map((day) => {
            const inMonth = viewMode === 'month' && day.getMonth() === month0;
            const isToday = isSameLocalDay(day, today);
            const dayEvents = eventSpans.filter(({ span }) => dayCoversSpan(day, span));
            return (
              <div
                key={day.getTime()}
                className={[
                  'project-cal-expanded-cell',
                  !inMonth ? 'project-cal-expanded-cell--outside' : '',
                  isToday ? 'project-cal-expanded-cell--today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="project-cal-expanded-daynum">{day.getDate()}</div>
                <div className="project-cal-expanded-tasks">
                  {dayEvents.slice(0, maxPerCell).map(({ ev, span }) => {
                    const multiDay = span.end.getTime() > span.start.getTime();
                    const isStart = isSpanStart(day, span);
                    const isEnd = isSpanEnd(day, span);
                    const range = multiDay
                      ? `${span.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${span.end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                      : '';
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        className={[
                          'project-cal-expanded-task',
                          multiDay ? 'appt-cal-event--span' : '',
                          multiDay && !isStart ? 'appt-cal-event--span-continued' : '',
                          multiDay && !isEnd ? 'appt-cal-event--span-continues' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ borderLeftColor: ev.color ?? '#3b82f6' }}
                        title={`${multiDay ? `${range}, starting ` : ''}${eventTime(ev.date)} — ${ev.title}${
                          ev.subtitle ? ` (${ev.subtitle})` : ''
                        }`}
                        onClick={ev.onClick}
                      >
                        {/* The time belongs to the first day only; later days just continue the bar. */}
                        {isStart ? <span className="appt-cal-event-time">{eventTime(ev.date)}</span> : null}{' '}
                        {ev.title}
                      </button>
                    );
                  })}
                  {dayEvents.length > maxPerCell ? (
                    <span className="project-cal-expanded-more">+{dayEvents.length - maxPerCell} more</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCalendar;
